/**
 * NOS-263: The one place for display formatting. Import from here — never
 * re-declare currency/date/relative-time helpers per page.
 */

/**
 * Formats cents as USD — "$1,234.56" by default, "$1,234" with digits: 0.
 * The digits variants are load-bearing: stat tiles round to whole dollars,
 * financial tables keep cents.
 */
export function formatCurrency(cents: number, opts: { digits?: 0 | 2 } = {}): string {
  const digits = opts.digits ?? 2
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

/** "Mar 4, 2026"; null/undefined/empty → "—". */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

/** "Today" / "Yesterday" / "4 days ago" / "2 weeks ago", then formatDate; null → "—". */
export function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "—"
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return "Today"
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  return formatDate(dateStr)
}

/** "Mar 4, 2026, 6:00 PM" with an optional "– Mar 5, 8:00 PM" end. */
export function formatEventDate(startDate: string, endDate: string | null): string {
  const formatted = new Date(startDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
  if (endDate) {
    const endFormatted = new Date(endDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
    return `${formatted} – ${endFormatted}`
  }
  return formatted
}
